import { DateTime } from 'luxon';
import { RecurringTransactionFrequency } from './api/recurring-transaction-types';
import { logAndThrowError } from '../utils/errors';
import { TransactionStatus } from '../transaction/api/transaction-types';
import { useMemo } from 'react';
import { useTimeZone } from '../user/api/use-get-user';
import { useRecurringTransactions } from './api/use-get-recurring-transactions';
/**
 * Options for selecting recurring transaction frequencies in the UI.
 * Each option pairs an enum value with its human-readable label.
 */
export const frequencySelectOptions = [
    { value: RecurringTransactionFrequency.DAILY, label: 'Daily' },
    { value: RecurringTransactionFrequency.WEEKLY, label: 'Weekly' },
    { value: RecurringTransactionFrequency.EVERY_2_WEEKS, label: 'Every 2 Weeks' },
    { value: RecurringTransactionFrequency.EVERY_3_WEEKS, label: 'Every 3 Weeks' },
    { value: RecurringTransactionFrequency.EVERY_4_WEEKS, label: 'Every 4 Weeks' },
    { value: RecurringTransactionFrequency.MONTHLY, label: 'Monthly' },
    { value: RecurringTransactionFrequency.EVERY_2_MONTHS, label: 'Every 2 Months' },
    { value: RecurringTransactionFrequency.EVERY_3_MONTHS, label: 'Every 3 Months' },
    { value: RecurringTransactionFrequency.EVERY_4_MONTHS, label: 'Every 4 Months' },
    { value: RecurringTransactionFrequency.EVERY_6_MONTHS, label: 'Every 6 Months' },
    { value: RecurringTransactionFrequency.YEARLY, label: 'Annually' },
    { value: RecurringTransactionFrequency.EVERY_2_YEARS, label: 'Every 2 Years' },
];
/**
 * Calculates and sets the next date from the base frequency.
 * Uses the user's timezone to ensure consistency with backend calculations.
 * If the base date is in the future, uses it directly as the next date.
 * Otherwise, advances the date until a future date is found.
 *
 * @param baseDate - The starting date
 * @param frequency - The recurring frequency pattern
 * @param timezone - The user's timezone
 * @returns The next occurrence date as an ISO date string, or null if calculation fails
 */
export const calculateNextDate = (baseDate, frequency, timezone) => {
    const today = DateTime.now().setZone(timezone).startOf('day');
    // Create a copy of baseDate with the correct timezone
    let baseDateWithZone = baseDate.setZone(timezone).startOf('day');
    // If the base date is already in the future or today, use it directly
    if (baseDateWithZone > today) {
        return baseDateWithZone.toISODate();
    }
    // Otherwise, start from the base date and keep advancing
    let calculatedDate = calculateNextOccurrence(baseDateWithZone, frequency);
    // Keep advancing until we get a future date
    while (calculatedDate < today) {
        calculatedDate = calculateNextOccurrence(calculatedDate, frequency);
    }
    return calculatedDate.toISODate();
};
/**
 * Calculates the next occurrence of a recurring transaction based on a base date and frequency.
 * Handles daily, weekly, and monthly-based frequencies with special handling for end-of-month dates.
 * Preserves the timezone of the input date.
 *
 * @param baseDate - The date to calculate from
 * @param frequency - The recurring frequency pattern
 * @returns The next occurrence date as a DateTime object
 * @throws Error if frequency is not supported
 *
 * @example
 * ```ts
 * const nextDate = calculateNextOccurrence(
 *   DateTime.fromISO('2024-01-31'),
 *   RecurringTransactionFrequency.MONTHLY
 * ); // Returns 2024-02-29
 * ```
 */
export const calculateNextOccurrence = (baseDate, frequency) => {
    switch (frequency) {
        case RecurringTransactionFrequency.DAILY:
            return baseDate.plus({ days: 1 });
        case RecurringTransactionFrequency.WEEKLY:
            return baseDate.plus({ weeks: 1 });
        case RecurringTransactionFrequency.EVERY_2_WEEKS:
            return baseDate.plus({ weeks: 2 });
        case RecurringTransactionFrequency.EVERY_3_WEEKS:
            return baseDate.plus({ weeks: 3 });
        case RecurringTransactionFrequency.EVERY_4_WEEKS:
            return baseDate.plus({ weeks: 4 });
        case RecurringTransactionFrequency.MONTHLY:
            return addMonths(baseDate, 1);
        case RecurringTransactionFrequency.EVERY_2_MONTHS:
            return addMonths(baseDate, 2);
        case RecurringTransactionFrequency.EVERY_3_MONTHS:
            return addMonths(baseDate, 3);
        case RecurringTransactionFrequency.EVERY_4_MONTHS:
            return addMonths(baseDate, 4);
        case RecurringTransactionFrequency.EVERY_6_MONTHS:
            return addMonths(baseDate, 6);
        case RecurringTransactionFrequency.YEARLY:
            return addMonths(baseDate, 12);
        case RecurringTransactionFrequency.EVERY_2_YEARS:
            return addMonths(baseDate, 24);
        default:
            return logAndThrowError(`Unsupported frequency: ${frequency}`);
    }
};
/**
 * Adds the specified number of months to a date while preserving end-of-month dates.
 * For example, Jan 31 + 1 month = Feb 28/29 (last day of month).
 * Preserves the timezone of the input date.
 *
 * @param baseDate - The starting date
 * @param months - Number of months to add
 * @returns A new DateTime with months added and end-of-month handling
 */
const addMonths = (baseDate, months) => {
    const isLastDayOfMonth = baseDate.day === baseDate.daysInMonth;
    let nextDate = baseDate.plus({ months });
    if (isLastDayOfMonth) {
        nextDate = nextDate.set({ day: nextDate.daysInMonth });
    }
    return nextDate;
};
/**
 * Calculates all instances of recurring transactions for a given month.
 * Uses the user's timezone to ensure consistency with backend calculations.
 * The returned transaction instances include a 'dateEpoch' field (seconds since epoch)
 * instead of an ISO date string.
 *
 * @param recurringTransactions - List of recurring transactions
 * @param year - Target year
 * @param month - Target month (1-12)
 * @param accountId - Optional account ID to filter transactions
 * @param timezone - Timezone
 * @returns Summary of recurring transactions for the month
 */
export function calculateMonthlyRecurrences(recurringTransactions, year, month, timezone, accountId) {
    const targetMonth = DateTime.fromObject({ year, month }, { zone: timezone });
    const startOfMonth = targetMonth.startOf('month');
    const endOfMonth = targetMonth.endOf('month');
    // Filter transactions by account if specified
    const filteredTransactions = accountId
        ? recurringTransactions.filter((tx) => tx.accountId === accountId)
        : recurringTransactions;
    const instances = [];
    // Generate all instances for each recurring transaction
    filteredTransactions.forEach((tx) => {
        if (!tx.nextDate)
            return;
        let currentDate = DateTime.fromISO(tx.nextDate, { zone: timezone });
        // Skip transaction if its next occurrence is after the target month
        if (currentDate > endOfMonth)
            return;
        // Advance the date until it reaches the target month if needed
        while (currentDate < startOfMonth) {
            currentDate = calculateNextOccurrence(currentDate, tx.frequency);
        }
        // Generate all occurrences within the month
        while (currentDate <= endOfMonth) {
            const dateEpoch = currentDate.toUnixInteger();
            instances.push({
                id: tx.id,
                dateEpoch,
                inflow: tx.inflow,
                outflow: tx.outflow,
                type: tx.type,
                payee: tx.payee,
                notes: tx.notes,
                status: TransactionStatus.PENDING,
                budgetId: tx.budgetId,
                bucketId: tx.bucketId,
                accountId: tx.accountId,
                transferAccountId: tx.transferAccountId,
                upcoming: true,
            });
            currentDate = calculateNextOccurrence(currentDate, tx.frequency);
        }
    });
    // Sort instances by dateEpoch in descending order.
    instances.sort((a, b) => b.dateEpoch - a.dateEpoch);
    const total = instances.reduce((sum, instance) => {
        const inflow = instance.inflow || 0;
        const outflow = instance.outflow || 0;
        return sum + inflow - outflow;
    }, 0);
    return {
        instances,
        total,
        count: instances.length,
    };
}
/**
 * Filters recurring transaction instances to return only future transactions
 * sorted in descending order by dateEpoch (latest first), then by ID (newest first).
 *
 * @param recurringInstances - Array of recurring transaction instances with a dateEpoch field
 * @param timezone - Timezone to use for date comparison
 * @return Array of future transaction instances sorted by dateEpoch and ID in descending order
 */
export function getFutureRecurringTransactions(recurringInstances, timezone) {
    const now = DateTime.now().setZone(timezone).startOf('day');
    // Filter for future instances by converting dateEpoch back to a DateTime
    const futureTransactions = recurringInstances.filter((instance) => {
        const instanceDate = DateTime.fromSeconds(instance.dateEpoch, { zone: timezone }).startOf('day');
        return instanceDate > now;
    });
    // Sort by dateEpoch (descending) then by id (descending)
    return futureTransactions.sort((a, b) => b.dateEpoch - a.dateEpoch);
}
/**
 * Hook that provides future recurring transactions for the specified month.
 *
 * @param year - Year to calculate recurrences for
 * @param month - Month to calculate recurrences for (1-12)
 * @returns Array of future transactions sorted by dateEpoch (latest first) and id (newest first)
 */
export function useFutureRecurringTransactions(year, month) {
    const recurringTransactions = useRecurringTransactions();
    const timezone = useTimeZone();
    return useMemo(() => {
        const now = DateTime.now().setZone(timezone);
        const viewMonth = DateTime.fromObject({ year, month }, { zone: timezone });
        if (viewMonth.startOf('month') < now.startOf('month')) {
            return [];
        }
        const { instances } = calculateMonthlyRecurrences(recurringTransactions, year, month, timezone);
        return getFutureRecurringTransactions(instances, timezone);
    }, [recurringTransactions, year, month, timezone]);
}
/**
 * Converts a regular transaction to a partial recurring transaction
 * to be used as a template for creating a new recurring transaction.
 *
 * @param transaction - The regular transaction to convert
 * @param timeZone - The user's timezone
 * @returns A partial recurring transaction with data copied from the original
 */
export function toRecurringTransaction(transaction, timeZone) {
    // Convert the transaction date to a startDate for the recurring transaction
    // If transaction.date is undefined, use the current date in the user's timezone
    const startDate = transaction.dateEpoch
        ? DateTime.fromSeconds(transaction.dateEpoch, { zone: timeZone }).toFormat('yyyy-MM-dd')
        : DateTime.now().setZone(timeZone).toFormat('yyyy-MM-dd');
    return {
        // Note: No ID provided so it will be treated as a new transaction
        accountId: transaction.accountId,
        type: transaction.type,
        payee: transaction.payee,
        notes: transaction.notes,
        bucketId: transaction.bucketId,
        transferAccountId: transaction.transferAccountId,
        inflow: transaction.inflow,
        outflow: transaction.outflow,
        startDate,
    };
}
